import type { ReactNode } from 'react'
import { sileo, type SileoPosition, type SileoStyles } from 'sileo'

/**
 * The app's toast.
 *
 * Everything imports `toast` from here rather than from the library directly,
 * so the library is named in one file. It has been swapped once already, and
 * the previous one was imported by nineteen components: the cost of that change
 * was nineteen edits and the risk that one of them used an API the replacement
 * did not have. Behind this the same swap is one file.
 *
 * The shape is deliberately the old one -- `toast.error('message', { ... })`,
 * title first -- because that is what every call site already reads like, and a
 * wrapper that changed the calling convention would have meant touching all of
 * them anyway.
 */

type ToastOptions = {
  description?: ReactNode | string
  position?: SileoPosition
  duration?: number | null
  icon?: ReactNode | null
  fill?: string
  styles?: SileoStyles
  roundness?: number
  autopilot?: boolean | { expand: number, collapse: number }
}

type ActionOptions = ToastOptions & {
  button?: { title: string, onClick: () => void }
}

/** What each state's toast is given, with the title it is titled by. */
type SileoOptions = ToastOptions & { title: string }

type PromiseStates<T> = {
  loading: SileoOptions
  success: SileoOptions | ((_data: T) => SileoOptions)
  error: SileoOptions | ((_err: unknown) => SileoOptions)
  action?: SileoOptions | ((_data: T) => SileoOptions)
  position?: SileoPosition
}

/**
 * The toast paints its own surface, so its text is set against that rather than
 * against the page. `normal-case` because a title here is a sentence somebody
 * wrote, not a label, and the library's own styling upper-cases it.
 */
const defaultStyles: SileoStyles = {
  title: 'text-background! normal-case!',
  description: 'text-background/75!',
}

// Caller styles win, key by key, so overriding the title does not silently
// drop the description's colour with it.
const withStyles = (options?: ToastOptions) => ({
  ...options,
  styles: { ...defaultStyles, ...options?.styles },
})

const toast = {
  success: (title: string, options?: ToastOptions) => sileo.success({ title, ...withStyles(options) }),
  error: (title: string, options?: ToastOptions) => sileo.error({ title, ...withStyles(options) }),
  info: (title: string, options?: ToastOptions) => sileo.info({ title, ...withStyles(options) }),
  warning: (title: string, options?: ToastOptions) => sileo.warning({ title, ...withStyles(options) }),
  action: (title: string, options?: ActionOptions) => sileo.action({ title, ...withStyles(options) }),
  show: (title: string, options?: ToastOptions) => sileo.show({ title, ...withStyles(options) }),
  promise: <T>(promise: Promise<T>, states: PromiseStates<T>) => sileo.promise(promise, states),
  dismiss: (id: string) => sileo.dismiss(id),
  clear: (position?: SileoPosition) => sileo.clear(position),
}

export { toast }
